import { ref } from "vue";
import { defaultApi } from "@/api";
import { logError, logInfo, logEvent, logMeasurement, FEATURE_NAMES, METRIC_TYPE } from "@/loggers";
import type { CreateDashboardRequest, CreateDashboardResponse } from "@/views/ControlTowerView/types";

export const useCreateDashboard = () => {
  const loading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Creates a new dashboard with the specified widgets
   * @param request - The dashboard creation request
   * @returns Promise<CreateDashboardResponse> The created dashboard
   */
  const createDashboard = async (request: CreateDashboardRequest): Promise<CreateDashboardResponse> => {
    const apiStart = performance.now();
    logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Creating dashboard: ${request.name}`);

    try {
      loading.value = true;
      error.value = null;
      const response = await defaultApi.apiVersion1.dashboards.createOne<CreateDashboardResponse>(request);

      logInfo(FEATURE_NAMES.MVS_CONTROL_TOWER, `Dashboard created successfully: ${response.dashboard_id}`);

      return response;
    } catch (err: any) {
      const errorMessage = err?.message || "Failed to create dashboard";
      error.value = errorMessage;

      logEvent(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_FAILURE, {
        api: "dashboards/createOne",
        error: errorMessage,
        statusCode: err?.response?.status
      });
      logError(FEATURE_NAMES.MVS_CONTROL_TOWER, `Error creating dashboard: ${errorMessage}`);

      throw err;
    } finally {
      logMeasurement(FEATURE_NAMES.MVS_CONTROL_TOWER, METRIC_TYPE.API_CALL_LATENCY, performance.now() - apiStart, {
        api: "dashboards/createOne"
      });
      loading.value = false;
    }
  };

  return {
    createDashboard
  };
};
